import { useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "@/constants";
import { formatTimestamp } from "@/lib/utils";
import { sendChatTfjs } from "@/lib/tfjsChat";
import { fuzzyMatch } from "@/lib/fuzzyMatch";
import { elysiaErr } from "@/lib/elysiaErr";
import { useChatStore } from "@/stores/useChatStore";
import { useAuthStore } from "@/stores/useAuthStore";
import nameSearch from "@/data/nameSearch.json";
import { generateAvgRateMatkulResponse } from "@/templates/avgRateMatkul";
import { generateBestProdiMatkulResponse } from "@/templates/bestProdiMatkul";
import { generateMyBestMatkulResponse } from "@/templates/myBestMatkul";
import { generateMyDosenMatkulResponse } from "@/templates/myDosenMatkul";
import { generateMyRateMatkulResponse } from "@/templates/myRateMatkul";
import { generateMyWorstMatkulResponse } from "@/templates/myWorstMatkul";
import { useLLMLimitStore } from "@/stores/llmLimitStore";
import type { UserData } from "@/types";
import { toast } from "sonner";

// Tipe response biar konsisten antara tfjs & gemini
type ChatResponse = {
  randomResponse: string;
  predictedTag: string;
  probability: number;
  model?: string;
};

export const useChatPresenter = () => {
  const user = useAuthStore((state) => state.user); // Ambil data user dari store
  const chatType = useChatStore((s) => s.chatType);
  const setChatType = useChatStore((s) => s.setChatType);
  const setMessages = useChatStore((s) => s.setMessages);
  const appendMessage = useChatStore((s) => s.appendMessage);
  const clearMessages = useChatStore((s) => s.clearMessages);
  const unlockTag = useChatStore((s) => s.unlockTag);
  // ... di dalam komponen/hook, panggil store
  const canUseLLM = useLLMLimitStore((s) => s.canUseLLM);
  const incrementUsage = useLLMLimitStore((s) => s.incrementUsage);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;

    const startTime = Date.now();

    appendMessage({
      id: startTime,
      content: message,
      role: "user",
      timestamp: formatTimestamp(startTime),
    });

    setIsLoading(true);
    setError(null);

    try {
      // TFJS + RAG
      // not need id: avg_rate_matkul, best_prodi_matkul
      let chatResult: ChatResponse;
      if (chatType === "tfjs") {
        chatResult = await sendChatTfjs(message);

        const resTfjsRag = await getTfjsResponse(
          chatResult.predictedTag,
          message,
          user
        );
        chatResult.randomResponse = resTfjsRag === "" ? chatResult.randomResponse : resTfjsRag;
      } else {
        if (user?.studentId && canUseLLM()) {
          chatResult = await tryLLMResponse(
            message,
            user
          );
          incrementUsage();
        } else {
          // baik karena tidak punya studentId, maupun karena limit LLM harian habis
          chatResult = await sendChatTfjs(message);

          const resTfjsRag = await getTfjsResponse(
            chatResult.predictedTag,
            message,
            user
          );
          chatResult.randomResponse = resTfjsRag === "" ? chatResult.randomResponse : resTfjsRag;
          // run ini 1 kali lalu kembali ke tfjs
          setChatType('tfjs');
        }
      }

      const { randomResponse, predictedTag: tag, probability, model } = chatResult;

      const duration = Math.round((Date.now() - startTime) / 1000);

      // 1. Deklarasikan variabel penampung di luar block scope
      let botResponse = "";

      // 2. Berikan nilai berdasarkan kondisi status login dan tag
      if (!user && tag === "rekomendasi") {
        // Catatan: Saya mengubah 'user' menjadi '!user' (jika TIDAK login) sesuai logika teks Anda
        botResponse = "Silakan login untuk mendapatkan rekomendasi matkul yang dipersonalisasi.";
      } else if (user && tag === "rekomendasi") {
        botResponse = randomResponse.replace("<username>", user.givenName); // Contoh personalisasi dengan nama user
      } else {
        botResponse = randomResponse;
      }

      appendMessage({
        id: Date.now(),
        content: botResponse,
        role: "assistant",
        tag: tag,
        timestamp: `${model ? model : 'tfjs'} | ${formatTimestamp(Date.now())} (${duration}s, ${tag} [${probability}%])`,
        // Tampilkan button modal hanya kalau tag rekomendasi
        showMatkulModal: tag === "rekomendasi" && !!user, // Pastikan user sudah login untuk rekomendasi
      });

      if (tag && tag !== "unknown") {
        unlockTag(tag);
      }
    } catch (err) {
      elysiaErr(err);
      setError("Terjadi kesalahan saat mengirim pesan. Silakan coba lagi.");
      appendMessage({
        id: Date.now(),
        content: "Maaf, terjadi kesalahan. Silakan coba lagi nanti.",
        role: "assistant",
        timestamp: formatTimestamp(Date.now()),
        isError: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    setMessages,
    clearMessages,
    isLoading,
    error,
    sendMessage,
  };
};

// ==== TFJS MODE ====
// Pattern toleransi typo:
// - matk?ul[a-z]* : menangkap matkul, matkult, matkulll, makul, matkuliah, dst.
// - mata\s*kuliah : menangkap mata kuliah
const prodiTarget = { // !!! update jika tabel prodi ada update
  "siskom": 1,
  "resiskom": 1,
  "rekayasa sistem komputer": 1,
  "sistem informasi": 0,
  "sisfo": 0,
}
const prodiTargetKeys = Object.keys(prodiTarget);

const REGEX_MATKUL = /\b\d+(?=\s*(?:matkul|maktul|mtkul|makul|mata\s*kuliah)\b)/i;
async function getTfjsResponse(
  predictedTag: string,
  userText: string,
  user: UserData | null
): Promise<string> {
  const studentId = user?.studentId;
  switch (predictedTag) {
    case "my_best_matkul": {
      if (!user) return "Login dulu untuk dapat cek matkul terbaik anda";
      if (!studentId) return "Maaf! gk ada data matkul terbaik anda.\n\nTanyakan hal lain atau login pakai email student!";
      const jumlahMatkul = Number(userText.match(REGEX_MATKUL)?.[0] ?? 0);
      const { data } = await axios.get(`${BACKEND_URL}/data/my_best_matkul/${studentId}`, {
        params: {
          jumlahMatkul: jumlahMatkul === 0 ? undefined : jumlahMatkul
        }
      });

      return generateMyBestMatkulResponse(data, jumlahMatkul);
    }

    case "my_worst_matkul": {
      if (!user) return "Login dulu untuk dapat cek matkul terlemah anda";
      if (!studentId) return "Maaf! gk ada data matkul gagal anda, email mu gk dikenali.\n\nTanyakan hal lain atau login pakai email student!";
      const jumlahMatkul = Number(userText.match(REGEX_MATKUL)?.[0] ?? 0);
      const { data } = await axios.get(`${BACKEND_URL}/data/my_worst_matkul/${studentId}`, {
        params: {
          jumlahMatkul: jumlahMatkul === 0 ? undefined : jumlahMatkul
        }
      });
      return generateMyWorstMatkulResponse(data, jumlahMatkul);
    }

    case "my_rate_matkul": {
      const matName = fuzzyMatch(userText, nameSearch['matkul']);
      if (!matName) return "Matkul apa itu?";
      if (!user) return `Saya dapat cek matkul ${matName}, tapi anda harus login dulu`;
      if (!studentId) return `Saya dapat cek matkul ${matName}, Tapi email mu gk dikenali (tidak ada riwayat database).\n\nTanyakan hal lain atau login pakai email student!`;

      try {
        const { data } = await axios.post(`${BACKEND_URL}/data/my_rate_matkul/${studentId}`, {
          matName
        });

        return generateMyRateMatkulResponse(data, matName);
      } catch {
        return generateMyRateMatkulResponse(null, matName);
      }
    }

    case "avg_rate_matkul": {
      const matLow = fuzzyMatch(userText, nameSearch['matkul']);
      if (!matLow) return "Apa nama matkul yang anda ingin cari?";
      const { jumlah, jenis, periodeText } = getPeriode(userText);
      const { data } = await axios.post(`${BACKEND_URL}/data/avg_rate_matkul`, {
        matLow,
        params: {
          jumlah,
          jenis
        },
      });
      return generateAvgRateMatkulResponse(data, matLow, periodeText);
    }

    case "my_dosen_matkul": {
      const dosenName = fuzzyMatch(userText, nameSearch['dosen']);
      if (!dosenName) return "Siapa nama dosen yang anda ingin cari?";
      if (!user) return `Saya dapat cek dosen ${dosenName}, tapi anda harus login dulu`;
      if (!studentId) return `Saya dapat cek dosen ${dosenName}, Tapi email mu gk dikenali (tidak ada riwayat database).\n\nTanyakan hal lain atau login pakai email student!`;

      const { data } = await axios.post(`${BACKEND_URL}/data/my_dosen_matkul/${studentId}`, {
        dosenName
      });
      return generateMyDosenMatkulResponse(data, dosenName);
    }

    case "best_prodi_matkul": {
      // 1: rsk, 2: si
      const prodiName = fuzzyMatch(userText, prodiTargetKeys);
      const jumlahMatkul = Number(userText.match(REGEX_MATKUL)?.[0] ?? 0);
      // console.log("jumlahMatkul", jumlahMatkul);

      if (!prodiName) return "Apa nama prodi yang anda cari?";

      const prodiId = prodiTarget[prodiName as keyof typeof prodiTarget];
      const { jumlah, jenis, periodeText } = getPeriode(userText);

      const { data } = await axios.get(`${BACKEND_URL}/data/best_prodi_matkul/${prodiId}`, {
        params: {
          jumlahMatkul: jumlahMatkul === 0 ? undefined : jumlahMatkul, jumlah, jenis
        }
      });
      // console.log('prodiId', prodiId);
      // prodi_id, total_matkul, matkul_list
      return generateBestProdiMatkulResponse(data, prodiName, jumlahMatkul, periodeText);
    }

    default:
      return "";
  }
}
function getPeriode(userText: string) {
  const match = userText.match(/(\d+)\s*(tahun|semester|smt)\b/i);

  const jumlah = match ? Number(match[1]) : undefined;
  const unit = match ? match[2].toLowerCase() : undefined;
  // Normalisasi unit ke 'tahun' atau 'semester'
  const jenis = unit === 'tahun' ? 'tahun' : (unit ? 'semester' : undefined);
  const periodeText = match ? `${match[1]} ${match[2]}` : undefined;

  return { jumlah, jenis, periodeText };
}

// ==== LLM MODE ====
async function tryLLMResponse(
  message: string,
  user: UserData
): Promise<ChatResponse> {
  const userName = Math.random() < 0.5 ? user.name : undefined;

  const pairs = buildProviderPairs();
  // untuk jika deteksi creator
  const chatResult: ChatResponse = await sendChatTfjs(message);

  for (const { sqlProvider, answerProvider } of pairs) {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/chat/ask`,
        {
          prompt: message,
          studentId: user.studentId,
          sqlProvider,
          answerProvider,
          predictedTag: chatResult.predictedTag,
          ...(userName && { userName }),
        },
        { headers: { "Content-Type": "application/json" } }
      );
      // console.log(res.data);
      if (res.data?.data) {
        const chatResultLLM: ChatResponse = res.data.data;
        chatResultLLM.model = `${res.data.meta.sqlProvider}+${res.data.meta.answerProvider}`;
        return chatResultLLM;
      }
    } catch (err) {
      elysiaErr(err, false);
      if (axios.isAxiosError(err)) {
        toast.error(`LLM Providers: ${err?.response?.data.provider}`);
      }
    }
  }

  // Semua kombinasi gagal → fallback terakhir ke tfjs
  const resTfjsRag = await getTfjsResponse(
    chatResult.predictedTag,
    message,
    user
  );
  chatResult.randomResponse = resTfjsRag === "" ? chatResult.randomResponse : resTfjsRag;
  useChatStore.getState().setChatType('tfjs');
  return chatResult;
};

type ProviderKey = "gemini" | "groq" | "openrouter" | "z-ai" | "github" | "mistral";

// z-ai gagal membentuk JSON terstruktur (json_schema strict) untuk jawaban akhir,
// TAPI endpoint SQL generator di backend memakai plain-text (callLLMText, bukan json_schema),
// jadi z-ai tetap aman dipakai sebagai sqlProvider — hanya dihindari sebagai answerProvider.
const SQL_MODELS: ProviderKey[] = ["gemini", "groq", "openrouter", "mistral", "z-ai"];
const ANSWER_MODELS: ProviderKey[] = ["gemini", "groq", "openrouter", "mistral"]; // github butuh kartu kredit, z-ai dihindari di sini

// Bangun daftar kombinasi (sqlProvider, answerProvider) yang sudah di-shuffle,
// sehingga tiap percobaan memakai 2 server LLM berbeda (beban tersebar).
function buildProviderPairs(): { sqlProvider: ProviderKey; answerProvider: ProviderKey }[] {
  const sqlPool = shuffleArray([...SQL_MODELS]);
  const answerPool = shuffleArray([...ANSWER_MODELS]);
  const attempts = Math.max(sqlPool.length, answerPool.length);

  const pairs: { sqlProvider: ProviderKey; answerProvider: ProviderKey }[] = [];
  for (let i = 0; i < attempts; i++) {
    const sqlProvider = sqlPool[i % sqlPool.length];
    let answerProvider = answerPool[i % answerPool.length];

    // jangan sampai sqlProvider === answerProvider, biar beban benar-benar
    // tersebar ke 2 server berbeda, bukan numpuk di satu server yang sama
    if (sqlProvider === answerProvider) {
      answerProvider = answerPool[(i + 1) % answerPool.length];
    }
    pairs.push({ sqlProvider, answerProvider });
  }
  return pairs;
}

function shuffleArray<T>(array: T[]): T[] {
  // Membuat salinan array agar tidak mengubah array asli (immutability)
  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    // Memilih indeks acak dari 0 hingga i
    const j = Math.floor(Math.random() * (i + 1));

    // Menukar elemen menggunakan teknik destructuring assignment TypeScript
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}